import apiService from 'app/apiService';
import type { Filters, InsightResult, MetricDataLevel, MetricFetchResult } from '../types';
import MetricsFrameworkMockService from './metricsFrameworkMockService';
import { mapTimePeriodToApi } from '../utils/mapTimePeriodToApi';
import { adaptMetricResult } from '../utils/adaptMetricResult';
import { adaptInsightResult } from '../utils/adaptInsightResult';
import { createTileFetchError, TILE_EMPTY_STATUS_CODE } from '../utils/tileFetchError';

export default class MetricsFrameworkApiService {
  static METRICS_BASE = 'api/v2/metrics';
  static INSIGHTS_BASE = 'api/v2/insights';

  static async getMetric(
    id: string,
    filters: Filters,
    useMock: boolean,
    dataLevel: MetricDataLevel = 'L1',
  ): Promise<MetricFetchResult> {
    if (!filters.dealerId) {
      throw new Error('Dealer ID is required');
    }

    if (useMock) {
      return MetricsFrameworkMockService.getMetric(id, filters, dataLevel);
    }

    const body = {
      ...mapTimePeriodToApi(filters.period),
      ...(dataLevel !== 'L1' ? { full_records: true } : {}),
    };
    const response = await apiService.post(
      `${this.METRICS_BASE}/${id}`,
      body,
      {
        params: {
          dealer_id: filters.dealerId,
        },
      },
      false,
    );

    if (response.statusCode === TILE_EMPTY_STATUS_CODE) {
      return {
        data: adaptMetricResult(response.body ?? { value: 0 }),
        statusCode: TILE_EMPTY_STATUS_CODE,
      };
    }

    if (response.statusCode && response.statusCode !== 200) {
      throw createTileFetchError(
        response.message || 'Failed to load metric',
        response.statusCode,
      );
    }
    if (!response.body) {
      throw createTileFetchError(
        response.message || 'No metric data received',
        response.statusCode,
      );
    }

    return { data: adaptMetricResult(response.body), statusCode: 200 };
  }

  static async getInsight(
    id: string,
    filters: Filters,
    useMock: boolean,
  ): Promise<InsightResult> {
    if (!filters.dealerId) {
      throw new Error('Dealer ID is required');
    }

    if (useMock) {
      const mock = await MetricsFrameworkMockService.getInsight(id, filters);
      return adaptInsightResult(mock);
    }

    const body = mapTimePeriodToApi(filters.period);
    const response = await apiService.post<unknown>(
      `${this.INSIGHTS_BASE}/${id}`,
      body,
      { params: { dealer_id: filters.dealerId } },
      false,
    );

    if (response.statusCode && response.statusCode !== 200) {
      throw createTileFetchError(
        response.message || 'Failed to load insight',
        response.statusCode,
      );
    }
    if (response.body == null) {
      throw createTileFetchError(
        response.message || 'No insight data received',
        response.statusCode,
      );
    }

    return adaptInsightResult(response.body);
  }
}
